import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppointmentDialog from "../components/AppointmentDialog";
import DeadlineDialog from "../components/DeadlineDialog";
import ClientSelect from "../components/ClientSelect";
import CategorySelect from "../components/CategorySelect";
import CategoryManager from "../components/CategoryManager";
import { APPT_TYPES, CAL_CATEGORIES, CAT_COLOR, apptCategory, timeToMin, minToTime, itDate, eur } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Search, Check, CreditCard, Tags } from "lucide-react";

const PXPM = 0.75;                 // pixels per minute
const DAY_H = 1440 * PXPM;         // full-day height
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const startOfWeek = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
const snap = (m, step = 15) => Math.round(m / step) * step;

function layout(events) {
  const res = {};
  const sorted = [...events].sort((a, b) => a._s - b._s || a._e - b._e);
  let cluster = [], clusterEnd = -1;
  const flush = () => {
    const cols = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < cols.length; i++) { if (cols[i] <= ev._s) { cols[i] = ev._e; res[ev.id] = { col: i }; placed = true; break; } }
      if (!placed) { cols.push(ev._e); res[ev.id] = { col: cols.length - 1 }; }
    });
    cluster.forEach((ev) => { res[ev.id].cols = cols.length; });
    cluster = [];
  };
  sorted.forEach((ev) => {
    if (cluster.length && ev._s >= clusterEnd) { flush(); clusterEnd = -1; }
    cluster.push(ev); clusterEnd = Math.max(clusterEnd, ev._e);
  });
  flush();
  return res;
}

export default function Calendario() {
  const { can } = useAuth();
  const nav = useNavigate();
  const canEdit = can("edit_calendar");
  const canPay = can("view_payments");
  const [view, setView] = useState(window.innerWidth < 768 ? "day" : "week");
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [teams, setTeams] = useState([]);
  const [clients, setClients] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [apptId, setApptId] = useState(null);
  const [now, setNow] = useState(new Date());
  const [visible, setVisible] = useState({ red: true, green: true, yellow: true, blue: true });
  const [teamFilter, setTeamFilter] = useState("");
  const [collabFilter, setCollabFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "sopralluogo" });
  const [dl, setDl] = useState({ open: false, occ: null, date: "" });
  const [search, setSearch] = useState({ open: false, q: "", clients: [], sel: null, appts: [], jobs: [] });
  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const drag = useRef(null);
  const lastPtr = useRef("mouse");
  const [ghost, setGhost] = useState(null);
  useEffect(() => {
    const block = (e) => { if (drag.current && drag.current.active) e.preventDefault(); };
    window.addEventListener("touchmove", block, { passive: false });
    return () => { window.removeEventListener("touchmove", block); };
  }, []);

  const teamOrder = useMemo(() => teams.map((t) => t.id), [teams]);
  const [cats, setCats] = useState([]);
  const [catMgr, setCatMgr] = useState(false);
  const loadCats = () => api.get("/calendar-categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { loadCats(); }, []);
  const colorMap = useMemo(() => ({ ...CAT_COLOR, ...Object.fromEntries(cats.map((c) => [`c:${c.id}`, c.color])) }), [cats]);
  const days = view === "day" ? [new Date(cursor)] : view === "week"
    ? Array.from({ length: 7 }, (_, i) => { const d = startOfWeek(cursor); d.setDate(d.getDate() + i); return d; })
    : [];
  const rangeFrom = days.length ? ymd(days[0]) : ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const rangeTo = days.length ? ymd(days[days.length - 1]) : ymd(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));

  const load = async () => {
    const mf = view === "month" ? ymd(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1)) : rangeFrom;
    const mt = view === "month" ? ymd(new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0)) : rangeTo;
    const { data } = await api.get(`/appointments?date_from=${mf}&date_to=${mt}`);
    setAppts(data.filter((a) => a.status !== "annullato"));
    if (canPay) { try { const r = await api.get(`/deadlines?date_from=${mf}&date_to=${mt}`); setDeadlines(r.data); } catch {} }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, rangeFrom, rangeTo]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (canEdit) { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); api.get("/collaborators").then((r) => setCollabs(r.data)).catch(() => {}); }
    else { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); }
  }, [canEdit]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 7 * 60 * PXPM; }, [view]);
  useEffect(() => {
    if (!open && !dl.open) { const t = setTimeout(() => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; }, 200); return () => clearTimeout(t); }
  }, [open, dl.open]);

  // ------- search -------
  useEffect(() => {
    if (!search.open) return;
    const t = setTimeout(async () => {
      if (search.q.trim().length < 1) { setSearch((s) => ({ ...s, clients: [] })); return; }
      try { const { data } = await api.get(`/clients?search=${encodeURIComponent(search.q.trim())}`); setSearch((s) => ({ ...s, clients: data })); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [search.q, search.open]);
  const pickSearchClient = async (c) => {
    try {
      const [a, j] = await Promise.all([api.get(`/appointments?client_id=${c.id}`), api.get(`/jobs?client_id=${c.id}`).catch(() => ({ data: [] }))]);
      setSearch((s) => ({ ...s, sel: c, appts: a.data, jobs: j.data }));
    } catch { setSearch((s) => ({ ...s, sel: c, appts: [], jobs: [] })); }
  };
  const gotoDate = (dstr) => { setCursor(parseYmd(dstr)); setView("day"); setSearch((s) => ({ ...s, open: false })); };

  // ------- filters -------
  const passFilters = (a) => {
    const cat = apptCategory(a, teamOrder);
    if (visible[cat] === false) return false;
    if (teamFilter && a.team_id !== teamFilter) return false;
    if (collabFilter && !(a.collaborator_ids || []).includes(collabFilter)) return false;
    return true;
  };
  const dayAppts = (d) => appts.filter((a) => a.date === ymd(d) && passFilters(a)).map((a) => {
    const s = timeToMin(a.start_time || "09:00");
    let e = a.end_time ? timeToMin(a.end_time) : s + 60;
    if (e <= s) e = s + 60;
    return { ...a, _s: s, _e: e, _cat: apptCategory(a, teamOrder) };
  });
  const dayDeadlines = (d) => visible.green ? deadlines.filter((x) => x.date === ymd(d)) : [];

  // ------- drag/create/move/resize -------
  const minutesFromY = (clientY) => {
    const rect = gridRef.current.getBoundingClientRect();
    const y = clientY - rect.top + scrollRef.current.scrollTop;
    return snap((y / DAY_H) * 1440);
  };
  const dayIndexFromX = (clientX) => {
    const rect = gridRef.current.getBoundingClientRect();
    const w = rect.width / days.length;
    return Math.max(0, Math.min(days.length - 1, Math.floor((clientX - rect.left) / w)));
  };
  const onGridPointerDown = (e, dayIdx) => {
    lastPtr.current = e.pointerType || "mouse";
    if (!canEdit || e.button !== 0 || lastPtr.current !== "mouse") return;
    const m = minutesFromY(e.clientY);
    drag.current = { mode: "create", dayIdx, startMin: m, endMin: m + 30, moved: false, active: true };
    setGhost({ mode: "create", dayIdx, s: m, e: m + 30 });
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const onEventPointerDown = (e, a, mode) => {
    if (!canEdit) { setApptId(a.id); return; }
    e.stopPropagation();
    const touch = e.pointerType && e.pointerType !== "mouse";
    if (touch && mode === "resize") return;
    const m = minutesFromY(e.clientY);
    const d = { mode, id: a.id, a, grab: m - a._s, dur: a._e - a._s, dayIdx: days.findIndex((x) => ymd(x) === a.date), moved: false, startMin: a._s, active: !touch, x0: e.clientX, y0: e.clientY };
    drag.current = d;
    if (touch) {
      d.timer = setTimeout(() => {
        if (drag.current !== d) return;
        d.active = true; d.grab = minutesFromY(d.y0) - a._s;
        if (navigator.vibrate) navigator.vibrate(30);
        setGhost({ mode: "move", id: a.id, dayIdx: d.dayIdx, s: a._s, e: a._e });
      }, 500);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };
  const endListeners = () => {
    window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); window.removeEventListener("pointercancel", onCancel);
  };
  const onCancel = () => {
    const d = drag.current; if (d && d.timer) clearTimeout(d.timer);
    drag.current = null; setGhost(null); endListeners();
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    if (!d.active) {
      if (Math.abs(e.clientX - d.x0) > 8 || Math.abs(e.clientY - d.y0) > 8) onCancel();
      return;
    }
    d.moved = true;
    const m = minutesFromY(e.clientY);
    if (d.mode === "create") { d.endMin = Math.max(d.startMin + 15, m); setGhost({ mode: "create", dayIdx: d.dayIdx, s: d.startMin, e: d.endMin }); }
    else if (d.mode === "move") { const ns = Math.max(0, Math.min(1440 - d.dur, m - d.grab)); const di = dayIndexFromX(e.clientX); setGhost({ mode: "move", id: d.id, dayIdx: di, s: ns, e: ns + d.dur }); d._ns = ns; d._di = di; }
    else if (d.mode === "resize") { const ne = Math.max(d.startMin + 15, m); setGhost({ mode: "resize", id: d.id, dayIdx: d.dayIdx, s: d.startMin, e: ne }); d._ne = ne; }
  };
  const onUp = async () => {
    const d = drag.current; drag.current = null; setGhost(null);
    endListeners();
    if (!d) return;
    if (d.timer) clearTimeout(d.timer);
    if (!d.active) return;
    if (d.mode === "create") {
      const ds = ymd(days[d.dayIdx]);
      if (!d.moved) { const s = d.startMin; openNew(ds, minToTime(s), minToTime(s + 60)); }
      else openNew(ds, minToTime(Math.min(d.startMin, d.endMin)), minToTime(Math.max(d.startMin, d.endMin)));
      return;
    }
    if (d.mode === "move") {
      if (!d.moved || d._ns == null) return;
      const newDate = ymd(days[d._di ?? d.dayIdx]);
      const body = { date: newDate, start_time: minToTime(d._ns), end_time: minToTime(d._ns + d.dur) };
      setAppts((prev) => prev.map((x) => x.id === d.id ? { ...x, ...body } : x));
      try { await api.put(`/appointments/${d.id}`, body); toast.success("Appuntamento spostato"); load(); }
      catch (e) { toast.error(e?.response?.data?.detail || "Spostamento non riuscito, ripristino."); load(); }
    } else if (d.mode === "resize") {
      if (d._ne == null) return;
      const body = { end_time: minToTime(d._ne) };
      setAppts((prev) => prev.map((x) => x.id === d.id ? { ...x, ...body } : x));
      try { await api.put(`/appointments/${d.id}`, body); toast.success("Durata aggiornata"); load(); }
      catch (e) { toast.error(e?.response?.data?.detail || "Modifica durata non riuscita, ripristino."); load(); }
    }
  };

  const openNew = (date, start, end) => { setForm({ type: "sopralluogo", status: "programmato", client_reminder_enabled: true, date, start_time: start, end_time: end }); setOpen(true); };
  const saveNew = async () => {
    if (!form.date || !form.start_time) return toast.error("Data e ora obbligatorie");
    try { await api.post("/appointments", { ...form, collaborator_ids: form.collaborator_ids || [] }); toast.success("Appuntamento creato"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const move = (dir) => { const d = new Date(cursor); if (view === "day") d.setDate(d.getDate() + dir); else if (view === "week") d.setDate(d.getDate() + 7 * dir); else d.setMonth(d.getMonth() + dir); setCursor(d); };
  const title = view === "month" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "day" ? cursor.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : `${days[0].getDate()} ${MONTHS[days[0].getMonth()].slice(0, 3)} – ${days[6].getDate()} ${MONTHS[days[6].getMonth()].slice(0, 3)} ${days[6].getFullYear()}`;

  // ---------- render helpers ----------
  const renderEvent = (a, pos) => {
    const g = ghost && ghost.id === a.id ? ghost : null;
    const s = g ? g.s : a._s, e = g ? g._e ?? g.e : a._e;
    const done = a.status === "completato";
    return (
      <div key={a.id} data-testid={`cal-event-${a.id}`}
        onPointerDown={(ev) => onEventPointerDown(ev, a, "move")}
        onClick={(ev) => { ev.stopPropagation(); if (!drag.current || !drag.current.moved) setApptId(a.id); }}
        onContextMenu={(ev) => ev.preventDefault()}
        className="absolute rounded-md px-1.5 py-0.5 text-white text-[11px] overflow-hidden cursor-pointer shadow-sm select-none"
        style={{ WebkitTouchCallout: "none", boxShadow: g ? "0 4px 12px rgba(0,0,0,.35)" : undefined, top: s * PXPM, height: Math.max(18, (e - s) * PXPM - 2), left: `${pos.col * (100 / pos.cols)}%`, width: `calc(${100 / pos.cols}% - 3px)`, background: colorMap[a._cat] || CAT_COLOR.other, opacity: done ? 0.6 : 1, zIndex: g ? 40 : 10 }}>
        <div className="flex items-center gap-1 font-semibold leading-tight">{done && <Check size={10} />}{a.start_time}</div>
        <div className="truncate leading-tight">{a.client_name || a.title || "—"}</div>
        {(e - s) > 45 && a.title && a.client_name && <div className="truncate opacity-80 leading-tight">{a.title}</div>}
        {canEdit && <div onPointerDown={(ev) => { ev.stopPropagation(); onEventPointerDown(ev, a, "resize"); }} className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize" />}
      </div>
    );
  };

  const renderTimeGrid = () => (
    <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden border border-border rounded-lg bg-white" style={{ position: "relative" }}>
      <div className="flex" style={{ height: DAY_H }}>
        <div className="w-12 shrink-0 relative border-r border-border">
          {Array.from({ length: 24 }, (_, h) => (<div key={h} className="absolute right-1 text-[10px] text-muted-foreground" style={{ top: h * 60 * PXPM - 6 }}>{h ? `${pad(h)}:00` : ""}</div>))}
        </div>
        <div ref={gridRef} className="flex-1 relative">
          <div className="absolute inset-0 flex">
            {days.map((d, di) => {
              const items = dayAppts(d); const lay = layout(items);
              const isToday = ymd(d) === ymd(new Date());
              return (
                <div key={di} data-testid={`cal-col-${ymd(d)}`} onPointerDown={(e) => onGridPointerDown(e, di)}
                  onClick={(e) => { if (canEdit && lastPtr.current !== "mouse") { const m = minutesFromY(e.clientY); openNew(ymd(d), minToTime(m), minToTime(m + 60)); } }}
                  className={`flex-1 relative border-r border-border ${isToday ? "bg-brand/5" : ""}`}>
                  {Array.from({ length: 24 }, (_, h) => (<div key={h} className="absolute left-0 right-0 border-t border-border/60" style={{ top: h * 60 * PXPM }} />))}
                  {Array.from({ length: 24 }, (_, h) => (<div key={`hh${h}`} className="absolute left-0 right-0 border-t border-dashed border-border/30" style={{ top: (h * 60 + 30) * PXPM }} />))}
                  {items.map((a) => renderEvent(a, lay[a.id] || { col: 0, cols: 1 }))}
                  {ghost && ghost.mode === "create" && ghost.dayIdx === di && (
                    <div className="absolute left-0 right-1 rounded-md bg-brand/40 border border-brand pointer-events-none" style={{ top: ghost.s * PXPM, height: Math.max(12, (ghost.e - ghost.s) * PXPM) }} />
                  )}
                  {isToday && (<div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: (now.getHours() * 60 + now.getMinutes()) * PXPM }}><div className="h-0.5 bg-red-600" /><div className="w-2 h-2 rounded-full bg-red-600 -mt-1 -ml-1" /></div>)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const AllDayStrip = () => (
    <div className="flex border-x border-t border-border rounded-t-lg bg-slate-50">
      <div className="w-12 shrink-0 text-[10px] text-muted-foreground flex items-center justify-end pr-1">scad.</div>
      <div className="flex-1 flex">
        {days.map((d, i) => (
          <div key={i} className="flex-1 border-r border-border p-1 min-h-[30px] space-y-0.5">
            {dayDeadlines(d).map((x) => (
              <button key={x.series_id + x.date} data-testid={`cal-deadline-${x.series_id}-${x.date}`} onClick={() => setDl({ open: true, occ: x, date: x.date })}
                className="w-full text-left rounded px-1 py-0.5 text-[10px] text-white truncate flex items-center gap-1" style={{ background: CAT_COLOR.green, opacity: x.paid ? 0.55 : 1 }}>
                {x.paid && <Check size={9} />}{eur(x.amount)} {x.description}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  const WeekHeader = () => (
    <div className="flex border-x border-t border-border bg-white">
      <div className="w-12 shrink-0 border-r border-border" />
      <div className="flex-1 flex">
        {days.map((d, i) => {
          const isToday = ymd(d) === ymd(new Date());
          return (
            <div key={i} className={`flex-1 text-center py-1.5 border-r border-border ${isToday ? "text-brand font-bold" : ""}`}>
              <div className="text-[10px] uppercase text-muted-foreground">{DOW[(d.getDay() + 6) % 7]}</div>
              <div className={`text-lg leading-none ${isToday ? "bg-brand text-white rounded-full w-7 h-7 flex items-center justify-center mx-auto mt-0.5" : ""}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // mini month calendar
  const MiniCal = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const off = (first.getDay() + 6) % 7;
    const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return (
      <div className="card-soft p-2" data-testid="mini-cal">
        <div className="flex items-center justify-between mb-1">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1"><ChevronLeft size={14} /></button>
          <span className="text-xs font-semibold capitalize">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1"><ChevronRight size={14} /></button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {DOW.map((d) => <div key={d} className="text-[9px] text-muted-foreground">{d[0]}</div>)}
          {Array.from({ length: off }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: dim }, (_, i) => {
            const d = new Date(cursor.getFullYear(), cursor.getMonth(), i + 1);
            const isToday = ymd(d) === ymd(new Date());
            const sel = ymd(d) === ymd(cursor);
            return <button key={i} onClick={() => setCursor(d)} className={`text-[11px] rounded-full w-6 h-6 mx-auto ${sel ? "bg-brand text-white" : isToday ? "text-brand font-bold" : "hover:bg-accent"}`}>{i + 1}</button>;
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 7.5rem)" }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h1 className="font-head text-xl sm:text-2xl font-extrabold">Calendario</h1>
          <Button variant="outline" size="icon" data-testid="btn-prev" onClick={() => move(-1)}><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="sm" data-testid="btn-today" onClick={() => setCursor(new Date())}>Oggi</Button>
          <Button variant="outline" size="icon" data-testid="btn-next" onClick={() => move(1)}><ChevronRight size={16} /></Button>
          <span className="font-head font-semibold capitalize hidden sm:inline">{title}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button variant="outline" size="sm" data-testid="btn-search" onClick={() => setSearch({ open: true, q: "", clients: [], sel: null, appts: [], jobs: [] })}><Search size={15} className="mr-1" /> Cliente</Button>
          {canPay && canEdit && <Button variant="outline" size="sm" data-testid="btn-new-deadline" onClick={() => setDl({ open: true, occ: null, date: ymd(cursor) })}><CreditCard size={15} className="mr-1" /> Scadenza</Button>}
          {canEdit && <Button variant="outline" size="sm" data-testid="btn-categories" onClick={() => setCatMgr(true)}><Tags size={15} className="mr-1" /> Categorie</Button>}
          {canEdit && <Button size="sm" className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-appt" onClick={() => openNew(ymd(cursor), "09:00", "10:00")}><Plus size={15} className="mr-1" /> Nuovo</Button>}
          <div className="flex gap-1 ml-1">
            {["day", "week", "month"].map((v) => (<button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className={`text-xs px-2.5 py-1.5 rounded-full border ${view === v ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>{v === "day" ? "Giorno" : v === "week" ? "Sett." : "Mese"}</button>))}
          </div>
        </div>
      </div>
      <p className="font-head font-semibold capitalize sm:hidden mb-2 text-sm">{title}</p>

      <div className="flex gap-3 flex-1 min-h-0">
        {/* Sidebar */}
        <div className="hidden lg:flex flex-col gap-3 w-56 shrink-0 overflow-y-auto">
          <MiniCal />
          <div className="card-soft p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Categorie</p>
            {CAL_CATEGORIES.map((c) => (
              <label key={c.key} data-testid={`legend-${c.key}`} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={visible[c.key]} onChange={() => setVisible((v) => ({ ...v, [c.key]: !v[c.key] }))} className="accent-current" style={{ accentColor: c.color }} />
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: c.color }} />
                <span>{c.label}</span>
              </label>
            ))}
            {cats.map((c) => (
              <label key={c.id} data-testid={`legend-c-${c.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={visible[`c:${c.id}`] !== false} onChange={() => setVisible((v) => ({ ...v, [`c:${c.id}`]: v[`c:${c.id}`] === false }))} style={{ accentColor: c.color }} />
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: c.color }} />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
            {canEdit && <button data-testid="legend-manage" onClick={() => setCatMgr(true)} className="text-xs text-brand hover:underline">+ Gestisci categorie</button>}
            <p className="text-[10px] text-muted-foreground pt-1">✓ = completato / pagato</p>
          </div>
          <div className="card-soft p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Filtri</p>
            <div><Label className="text-xs">Squadra</Label>
              <Select value={teamFilter || "all"} onValueChange={(v) => setTeamFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-team" className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}><SelectItem value="all">Tutte</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Collaboratore</Label>
              <Select value={collabFilter || "all"} onValueChange={(v) => setCollabFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-collab" className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}><SelectItem value="all">Tutti</SelectItem>{collabs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 flex flex-col min-h-0">
          {view === "month" ? <MonthView cursor={cursor} appts={appts} deadlines={deadlines} teamOrder={teamOrder} colorMap={colorMap} visible={visible} passFilters={passFilters} onEvent={setApptId} onDeadline={(x) => setDl({ open: true, occ: x, date: x.date })} onDay={(ds) => { setCursor(parseYmd(ds)); setView("day"); }} />
            : (<><WeekHeader /><AllDayStrip />{renderTimeGrid()}</>)}
        </div>
      </div>

      <CategoryManager open={catMgr} onClose={() => setCatMgr(false)} categories={cats} onChanged={() => { loadCats(); load(); }} />
      <AppointmentDialog appointmentId={apptId} categories={cats} open={!!apptId} onClose={() => setApptId(null)} onChanged={() => { load(); if (search.sel) pickSearchClient(search.sel); }} />
      <DeadlineDialog open={dl.open} occ={dl.occ} defaultDate={dl.date} canEdit={canEdit && canPay} onClose={() => setDl({ open: false, occ: null, date: "" })} onChanged={load} />

      {/* Search dialog */}
      <Dialog open={search.open} onOpenChange={(o) => setSearch((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="calendar-search-dialog">
          <DialogHeader><DialogTitle className="font-head">Cerca per cliente</DialogTitle></DialogHeader>
          <Input autoFocus data-testid="calendar-search-input" placeholder="Nome, telefono o località…" value={search.q} onChange={(e) => setSearch((s) => ({ ...s, q: e.target.value, sel: null }))} />
          {!search.sel && search.clients.map((c) => (<button key={c.id} data-testid={`search-client-${c.id}`} onClick={() => pickSearchClient(c)} className="w-full text-left p-2.5 rounded-lg hover:bg-accent text-sm"><span className="font-medium">{c.name}</span> <span className="text-muted-foreground">· {c.locality || c.phone}</span></button>))}
          {!search.sel && search.q.trim() && search.clients.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nessun cliente.</p>}
          {search.sel && (
            <div className="space-y-3">
              <div className="flex items-center justify-between"><p className="font-head font-bold">{search.sel.name}</p><button onClick={() => setSearch((s) => ({ ...s, sel: null }))} className="text-xs text-brand underline">Cambia</button></div>
              <p className="text-xs font-semibold text-muted-foreground">Appuntamenti ({search.appts.length})</p>
              {search.appts.map((a) => (
                <div key={a.id} data-testid={`search-appt-${a.id}`} className="card-soft p-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="text-sm font-medium capitalize truncate">{a.type} {a.status === "completato" ? "· svolto" : a.status === "annullato" ? "· annullato" : ""}</p><p className="text-xs text-muted-foreground">{itDate(a.date)} · {a.start_time}</p></div>
                  <div className="flex gap-1 shrink-0"><Button size="sm" variant="outline" onClick={() => setApptId(a.id)}>Apri</Button><Button size="sm" variant="outline" onClick={() => gotoDate(a.date)}>Vai</Button></div>
                </div>
              ))}
              <p className="text-xs font-semibold text-muted-foreground">Lavori ({search.jobs.length})</p>
              {search.jobs.map((j) => (<button key={j.id} data-testid={`search-job-${j.id}`} onClick={() => { setSearch((s) => ({ ...s, open: false })); nav(`/lavori/${j.id}`); }} className="w-full text-left card-soft p-2.5"><p className="text-sm font-medium">{j.product}</p><p className="text-xs text-muted-foreground capitalize">{j.status}</p></button>))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New appointment */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="new-appt-dialog">
          <DialogHeader><DialogTitle className="font-head">Nuovo appuntamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="appt-type"><SelectValue /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{APPT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Categoria (colore)</Label><CategorySelect testid="appt-category" value={form.category_id} categories={cats} onChange={(v) => setForm({ ...form, category_id: v })} /></div>
            <div><Label>Cliente</Label>
              <ClientSelect testid="appt-client" value={form.client_id} clients={clients} onCreated={(c) => setClients((l) => [...l, c].sort((a, b) => a.name.localeCompare(b.name)))}
                onPick={(c) => setForm((f) => ({ ...f, client_id: c?.id, address: f.address || c?.address, locality: f.locality || c?.locality }))} />
            </div>
            <div><Label>Titolo / descrizione</Label><Input data-testid="appt-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="es. Montaggio pergola" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Data</Label><Input type="date" data-testid="appt-date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Inizio</Label><Input type="time" data-testid="appt-start" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div><Label>Fine</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
            </div>
            <div><Label>Indirizzo</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Squadra</Label>
              <Select value={form.team_id || ""} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger data-testid="appt-team"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Collaboratori</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">{collabs.map((c) => { const sel = (form.collaborator_ids || []).includes(c.id); return (<button key={c.id} type="button" onClick={() => { const cur = form.collaborator_ids || []; setForm({ ...form, collaborator_ids: sel ? cur.filter((x) => x !== c.id) : [...cur, c.id] }); }} className={`text-xs px-2.5 py-1 rounded-full border ${sel ? "bg-brand text-white border-brand" : "bg-white text-muted-foreground"}`}>{c.name}</button>); })}</div>
            </div>
            <div><Label>Note operative</Label><Textarea value={form.operational_notes || ""} onChange={(e) => setForm({ ...form, operational_notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="appt-save" onClick={saveNew}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MonthView({ cursor, appts, deadlines, teamOrder, colorMap, visible, passFilters, onEvent, onDeadline, onDay }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const off = (first.getDay() + 6) % 7;
  const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < off; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  const ymdl = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return (
    <div className="flex-1 overflow-y-auto border border-border rounded-lg bg-white">
      <div className="grid grid-cols-7 sticky top-0 bg-slate-50 z-10">{DOW.map((d) => <div key={d} className="text-xs font-semibold text-center py-1.5 text-muted-foreground border-b border-border">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="border-b border-r border-border min-h-[96px]" />;
          const ds = ymdl(d);
          const isToday = ds === ymdl(new Date());
          const evs = appts.filter((a) => a.date === ds && a.status !== "annullato" && passFilters(a)).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
          const dls = visible.green ? deadlines.filter((x) => x.date === ds) : [];
          return (
            <div key={i} data-testid={`month-cell-${ds}`} onClick={() => onDay(ds)} className={`border-b border-r border-border min-h-[96px] p-1 cursor-pointer hover:bg-accent/40 ${isToday ? "bg-brand/5" : ""}`}>
              <div className={`text-xs mb-0.5 ${isToday ? "font-bold text-brand" : "text-muted-foreground"}`}>{d.getDate()}</div>
              {dls.slice(0, 2).map((x) => <button key={x.series_id + x.date} onClick={(e) => { e.stopPropagation(); onDeadline(x); }} className="w-full text-left rounded px-1 text-[10px] text-white truncate mb-0.5" style={{ background: CAT_COLOR.green, opacity: x.paid ? 0.55 : 1 }}>{eur(x.amount)} {x.description}</button>)}
              {evs.slice(0, 3).map((a) => (<button key={a.id} onClick={(e) => { e.stopPropagation(); onEvent(a.id); }} className="w-full text-left rounded px-1 text-[10px] text-white truncate mb-0.5" style={{ background: colorMap[apptCategory(a, teamOrder)] || CAT_COLOR.other, opacity: a.status === "completato" ? 0.6 : 1 }}>{a.start_time} {a.client_name || a.title}</button>))}
              {(evs.length + dls.length) > 5 && <div className="text-[10px] text-muted-foreground">+{evs.length + dls.length - 5}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
